import { DatePipe, Location } from '@angular/common';
import { Component, input, signal, inject, computed, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { verifyEmail } from '../../../core/store/profile/profile.actions';

import * as fromSportCenterActions from '../../../core/store/quick-register/quick-register.actions';

import { UtilService } from '../../../core/utils/utils.service';

import { ToastController } from '@ionic/angular';
import { InputMaskLibModule } from '@libs/input-mask';
import { TranslationLibModule } from '@libs/translation';

import { selectIsEmailValid } from '../../../core/store/profile/profile.selectors';
import {
  selectIsLoading,
  selectUserState,
} from '../../../core/store/quick-register/quick-register.selectors';
import { CrudProfileLibModule } from '../../crud-profile-lib.module';

import { getAuthPath, QuickRegisterRequest, Term } from '@libs/entity-lib';
import { getPreviousUrl, selectOnlySportCenterId } from '@libs/settings-state';

import { MaskitoDirective } from '@maskito/angular';

import { DialogService } from '@libs/util-services';
import { PlayerRegistrationService } from '@libs/util-ui';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { take, tap } from 'rxjs';
import { CrudLessorLibService } from '../../../core/services/crud-lessor/crud-lessor-lib.service';
import { addIcons } from 'ionicons';
import { shieldCheckmarkOutline, chevronUpOutline, chevronDownOutline, businessOutline, alertCircleOutline } from 'ionicons/icons';
import { LibPhoneInputComponent } from '@libs/phone-input';
import { cpfMaskitoOptions, cpfPredicate } from '../../../core/masks/cpf-mask';

import { IonButton, IonCheckbox, IonIcon, IonInput, IonNote, IonSpinner } from '@ionic/angular/standalone';

@Component({
    selector: 'crud-quick-register-form',
    templateUrl: 'quick-register-form.component.html',
    styleUrls: ['quick-register-form.component.scss'],
    imports: [
    IonButton,
    IonCheckbox,
    IonIcon,
    IonInput,
    IonNote,
    IonSpinner,
    ReactiveFormsModule,
    TranslationLibModule,
    InputMaskLibModule,
    CrudProfileLibModule,
    MaskitoDirective,
    LibPhoneInputComponent
  ],
    providers: [DatePipe, UtilService, DialogService]
})
export class QuickRegisterFormComponent {
  private readonly store = inject(Store);
  private readonly utilService = inject(UtilService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly actions = inject(Actions);
  private readonly location = inject(Location);
  private readonly crudService = inject(CrudLessorLibService);
  private readonly playerRegistration = inject(PlayerRegistrationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastController = inject(ToastController);

  readonly isAthlete = input<boolean>(true);

  /**
   * false quando o form vive num MODAL (ex.: cadastro empilhado sobre o
   * find-player): o host fecha o modal no sucesso; um location.back() aqui
   * navegaria a página POR BAIXO dos modais.
   */
  readonly navigateOnSuccess = input<boolean>(true);

  // Convite do CE: quando o atleta abre /atleta/register/quick-register?ce=<username>,
  // resolvemos o CE para pré-vincular (vira cliente ao concluir) e exibir quem convidou.
  readonly invitedCenter = signal<{ id: number; name: string; image?: string } | null>(null);
  // Veio ?ce= mas não resolveu (username inválido/CE inativo) → avisa em vez de degradar mudo.
  readonly inviteError = signal(false);

  form: FormGroup;

  readonly isLoading = this.store.selectSignal(selectIsLoading);
  readonly userState = this.store.selectSignal(selectUserState);
  readonly isEmailInValid = this.store.selectSignal(selectIsEmailValid);
  readonly getPreviousUrl = this.store.selectSignal(getPreviousUrl);
  readonly sportCenterId = this.store.selectSignal(selectOnlySportCenterId);

  readonly showPassword = signal(false);

  readonly cpfMaskitoOptions = cpfMaskitoOptions;
  readonly cpfPredicate = cpfPredicate;

  readonly savePassword = signal(false);
  /** true = documento estrangeiro: o campo cpf passa a carregar o passaporte. */
  readonly foreignDocument = signal(false);
  readonly acceptedTerms = signal(false);
  readonly terms = signal<Term[]>([]);
  readonly loadingTerms = signal(false);
  readonly termsExpanded = signal(false);
  readonly hasTerms = computed(() => this.terms().length > 0);

  constructor() {
    addIcons({ shieldCheckmarkOutline, chevronUpOutline, chevronDownOutline, businessOutline, alertCircleOutline });
    this.form = this.formBuilder.group({
      // Formato do celular validado pelo próprio <lib-phone-input> (NG_VALIDATORS);
      // o componente já abre com o Brasil selecionado — sem seed de "+55 ".
      fullName: ['', [Validators.required, Validators.minLength(3)]],
      cellphone: ['', [Validators.required]],
      cpf: ['', [Validators.required, this.utilService.cpfValidator]],
      email: new FormControl('', [Validators.required, Validators.email]),
      birthDate: [''],
      password: [''],
    });

    const userState = this.userState();
    if (userState) {
      this.form.patchValue(userState);
    }

    effect(() => {
      const isAthlete = this.isAthlete();
      this.updatePasswordValidator();
      if (isAthlete) {
        this.loadTerms();
      }
    });

    this.resolveInvitingSportCenter();
  }

  /** Lê ?ce=<username> e resolve o CE que convidou (id + nome + logo). */
  private resolveInvitingSportCenter(): void {
    const ce = this.route.snapshot.queryParamMap.get('ce')?.trim();
    if (!ce) {
      return;
    }
    this.crudService.fetchPublicSportCenter(ce).pipe(take(1)).subscribe({
      next: (center) => {
        if (center?.id) {
          this.invitedCenter.set(center);
          this.inviteError.set(false);
        } else {
          this.inviteError.set(true);
        }
      },
      error: () => this.inviteError.set(true),
    });
  }

  /** "Já tenho conta" → vai para o login (mantém ?ce= no contexto se houver). */
  goToLogin(): void {
    this.router.navigate([getAuthPath()]);
  }

  /**
   * Alterna CPF ↔ passaporte (PLANO_CLIENTE_INTERNACIONAL, Fase 2). O mesmo
   * control `cpf` carrega o documento nos dois modos — o que muda é o
   * validador (mod-11 vs. alfanumérico 5–50) e a máscara, trocada no template.
   * O valor é zerado na troca: um documento não vale no formato do outro.
   */
  toggleForeignDocument(checked: boolean): void {
    this.foreignDocument.set(checked);
    const cpf = this.form.get('cpf');
    if (!cpf) {
      return;
    }
    cpf.setValue('');
    cpf.setValidators(
      checked
        ? [Validators.required, Validators.pattern('[A-Za-z0-9]{5,50}')]
        : [Validators.required, this.utilService.cpfValidator]
    );
    cpf.updateValueAndValidity();
    cpf.markAsPristine();
    cpf.markAsUntouched();
  }

  registerUser() {
    // O botão fica SEMPRE habilitado (exceto enquanto envia). Em vez de travar o
    // botão e deixar o atleta adivinhando o porquê, validamos no clique e dizemos
    // exatamente o que falta. Cada guard mostra uma mensagem clara e específica.
    if (this.isLoading()) {
      return;
    }

    if (!this.form.valid) {
      // Marca os campos (erros inline) E avisa por toast com a mensagem ESPECÍFICA do
      // primeiro campo inválido — os campos com erro podem estar fora da área visível,
      // então um toast genérico não ajuda o atleta a saber o que corrigir.
      this.markAsDirty();
      this.presentError(this.firstInvalidFieldMessage());
      return;
    }

    if (this.isEmailInValid()) {
      this.presentError('Este e-mail já tem conta. Toque em "entrar" ou use outro e-mail.');
      return;
    }

    if (this.isAthlete() && this.hasTerms() && !this.acceptedTerms()) {
      this.presentError('Aceite os termos de uso para continuar.');
      return;
    }

    const owner = {
      ...this.form.getRawValue(),
      // O BE valida por tipo e, em PASSPORT, não passa o strip de dígitos do
      // CPF por cima do documento (destruiria o alfanumérico).
      documentType: this.foreignDocument() ? 'PASSPORT' : 'CPF',
    };
    // Atleta convidado por um CE (?ce=) → manda o sportCenterId p/ virar cliente
    // do centro já no cadastro. Lessor cadastrando cliente usa o CE do contexto.
    const athleteSportCenterId = this.invitedCenter()?.id;
    const register: QuickRegisterRequest = {
      owner,
      sportCenterId: this.isAthlete()
        ? athleteSportCenterId ?? undefined
        : this.sportCenterId() ?? undefined,
      acceptedTerms: this.isAthlete() ? this.acceptedTerms() : true,
    };
    this.store.dispatch(
      fromSportCenterActions.quickRegisterProfile({
        profileState: register
      })
    );
    this.listenerRequestRentEvents();
  }

  /**
   * Retorna a mensagem clara do PRIMEIRO campo inválido, na ordem em que aparecem no
   * formulário. Assim o toast diz exatamente o que falta corrigir em vez de um texto
   * genérico. Os demais erros continuam visíveis inline (via markAsDirty).
   */
  private firstInvalidFieldMessage(): string {
    const order = ['fullName', 'cellphone', 'cpf', 'email', 'password'];
    for (const name of order) {
      const control = this.form.get(name);
      if (!control || control.disabled || control.valid) {
        continue;
      }
      const errors = control.errors ?? {};
      switch (name) {
        case 'fullName':
          if (errors['required']) return 'Informe o nome completo.';
          if (errors['minlength']) return 'O nome deve ter ao menos 3 caracteres.';
          break;
        case 'cellphone':
          return 'Informe um celular válido com DDD (ex.: (34) 99999-9999).';
        case 'cpf':
          if (this.foreignDocument()) {
            if (errors['required']) return 'Informe o número do passaporte.';
            return 'Passaporte inválido: use de 5 a 50 letras e números.';
          }
          if (errors['required']) return 'Informe o CPF.';
          return 'CPF inválido. Verifique os números digitados.';
        case 'email':
          if (errors['required']) return 'Informe o e-mail.';
          if (errors['email']) return 'E-mail inválido. Verifique o endereço digitado.';
          break;
        case 'password':
          if (errors['required']) return 'Informe a senha.';
          if (errors['minlength']) return 'A senha deve ter ao menos 6 caracteres.';
          break;
      }
    }
    return 'Preencha os campos destacados para concluir o cadastro.';
  }

  private async presentError(message: string) {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      position: 'bottom',
      color: 'danger',
    });
    await toast.present();
  }

  onEmailChange(): void {
    const isValid = this.form.get('email')?.valid;
    if (isValid) {
      const email = this.getEmailFromForm();
      this.store.dispatch(verifyEmail({ email }));
    }
  }

  markAsDirty() {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }

  getLocale() {
    return this.utilService.getLocale();
  }

  private getEmailFromForm() {
    return this.form.get('email')?.value?.trim() ?? '';
  }

  isCPFInvalid(): boolean {
    const cpfValue = this.form.get('cpf')?.value;
    const invalidCPFFormat = this.form.get('cpf')?.errors?.['invalidCpf'];
    return this.utilService.isCPFInvalid(cpfValue, invalidCPFFormat);
  }

  getBackTextWithTranslation() {
    return this.translate.instant('BACK');
  }

  togglePasswordVisibility() {
    const showPassword = this.showPassword();
    this.showPassword.set(!showPassword);
  }

  private updatePasswordValidator() {
    const passwordControl = this.form.get('password');

    if (this.isAthlete()) {
      passwordControl?.setValidators([Validators.required, Validators.minLength(6)]);
    } else {
      passwordControl?.clearValidators();
    }

    passwordControl?.updateValueAndValidity();
  }

  private listenerRequestRentEvents() {
    this.actions
      .pipe(
        ofType(fromSportCenterActions.quickRegisterProfileSuccefully),
        take(1),
        tap(({ sessionEstablished }) => {
          if(this.isAthlete()) {
            if (this.savePassword()) {
              localStorage.setItem('savedUsername', this.form.get('email')?.value);
              localStorage.setItem('savedPassword', this.form.get('password')?.value);
              localStorage.setItem('rememberUsername', 'true');
            } else {
              localStorage.removeItem('savedUsername');
              localStorage.removeItem('savedPassword');
              localStorage.removeItem('rememberUsername');
            }
          } else {
            const email = this.form.get('email')?.value;
            if (email) {
              this.playerRegistration.notifyRegistered(email);
            }
          }
          this.resetForm();
          // Hospedado em modal: quem fecha é o host (via onRegistered).
          if (!this.navigateOnSuccess()) {
            return;
          }
          // Atleta cadastrado mas SEM sessão confirmada (cookie não veio ou o
          // /signin/me recusou): manda pro login em vez de devolvê-lo a uma tela
          // logada que não responde. As credenciais salvas acima pré-preenchem o
          // form. O toast que explica o passo já veio do effect.
          if (this.isAthlete() && !sessionEstablished) {
            this.goToLogin();
            return;
          }
          // Convite por deep link não tem histórico de "voltar" → vai p/ a home.
          if (this.isAthlete() && this.invitedCenter()) {
            this.router.navigate(['/']);
          } else {
            this.location.back();
          }
        })
      )
      .subscribe();
  }

  private loadTerms() {
    this.loadingTerms.set(true);
    this.crudService.fetchTerms().pipe(take(1)).subscribe({
      next: (terms) => {
        this.terms.set(terms);
        this.loadingTerms.set(false);
      },
      error: () => {
        this.loadingTerms.set(false);
      }
    });
  }

  toggleTerms(): void {
    this.termsExpanded.set(!this.termsExpanded());
  }

  resetForm() {
    // Redefine todos os campos do formulário para os valores iniciais
    this.form.reset({
      fullName: '',
      cellphone: '',
      cpf: '',
      email: '',
      password: ''
    });

    // Redefine os estados de toque e sujeira do formulário
    this.form.markAsPristine();
    this.form.markAsUntouched();

    // Adicionalmente, redefina individualmente os campos se necessário
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.setErrors(null);
    });
  }
  
}
